import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { MatTooltipModule } from '@angular/material/tooltip';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { finalize, Subscription, tap, Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { AdminStudentPlatformHeaderComponent } from '../../admin-student-platform/admin-student-platform-header/admin-student-platform-header.component';
import { GetAllCompany } from '@edtech-ui/core/models/company-management/get-all-companies-list';
import { CompanyService } from '@edtech-ui/core/services/company-management/company.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CompanyApprovalPayload } from '@edtech-ui/core/models/company-management/accept-reject-company-approval';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
	selector: 'app-admin-company-approval',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		AdminStudentPlatformHeaderComponent,
		MatTooltipModule,
		MatProgressSpinnerModule
	],
	templateUrl: './admin-company-approval.component.html',
	styleUrls: ['./admin-company-approval.component.scss']
})
export class AdminCompanyApprovalComponent implements OnInit, OnDestroy {

	searchTerm = '';
	companiesData: GetAllCompany[] = [];
	allCompaniesData: any[] = [];
	selectedCompany: GetAllCompany | null = null;
	showApproveModal = false;
	showRejectModal = false;
	showClarificationModal = false;
	showViewReasonModal = false;
	showViewClarificationModal = false;
	clarificationReason = '';
	showDetailedView = false;
	isApprovalLoading: boolean = false;
	rejectionReason = '';
	total = 0;
	page = 1;
	limit = 10;
	totalPages = 1;
	status = 'pending,need_clarification,rejected';
	isLoading = false;
	sortField = '';
	sortDirection: 'asc' | 'desc' = 'asc';
	subscription = new Subscription();
	showingFrom = 0;
	showingTo = 0;
	selectedDate = '';
	selectedIndustry = '';
	industries: string[] = [];
	private filterSubject = new Subject<void>();
	searchSubscription = new Subscription();
	searchSubject = new Subject<string>();


	constructor(
		private toastr: ToastrService,
		private companyService: CompanyService,
		private router: Router,
		private route: ActivatedRoute
	) { }


	ngOnInit(): void {
		const initialPage = Number(this.route.snapshot.queryParams['page']);
		if (initialPage && !isNaN(initialPage) && initialPage > 0) {
			this.page = initialPage;
		}
		this.searchSubscription.add(
			this.searchSubject.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => {
				this.page = 1;
				this.getAllCompany();
			})
		);
		this.loadIndustrySuggestions();
		this.getAllCompany();

		this.subscription.add(
			this.route.queryParams.subscribe(params => {
				const queryPage = Number(params['page']);
				if (queryPage && !isNaN(queryPage) && queryPage > 0 && queryPage !== this.page) {
					this.page = queryPage;
					this.getAllCompany();
				}
			})
		);
	}

	loadIndustrySuggestions(): void {
		this.subscription.add(
			this.companyService.getAllCompaniesList({ limit: 100 }).subscribe({
				next: (response) => {
					if (response.Success && response.Data?.companies) {
						this.industries = Array.from(
							new Set(response.Data.companies.map((c: any) => c.industry).filter(Boolean))
						).sort() as string[];
					}
				}
			})
		);
	}

	prevPage(): void {
		if (this.page > 1) {
			this.page--;
			this.getAllCompany();
		}
	}

	nextPage(): void {
		if (this.page < this.totalPages) {
			this.page++;
			this.getAllCompany();
		}
	}


	getAllCompany(): void {
		this.isLoading = true;
		const payload = {
			status: this.status || undefined,
			search: this.searchTerm?.trim() || undefined,
			submitted_at: this.selectedDate || undefined,
			industry: this.selectedIndustry || undefined,
			page: this.page,
			limit: this.limit
		};

		this.subscription.add(
			this.companyService.getAllCompaniesList(payload).pipe(
				finalize(() => {
					this.isLoading = false;
				})
			).subscribe({
				next: (response) => {
					console.log('API RESPONSE', response);

					if (response.Success) {
						this.total = response.Data.total;
						this.page = response.Data.page;
						this.limit = response.Data.limit;
						this.totalPages = response.Data.total_pages;
						this.companiesData = response?.Data?.companies || [];
						this.showingFrom = this.total === 0 ? 0 : ((this.page - 1) * this.limit) + 1;
						this.showingTo = Math.min(this.page * this.limit, this.total);
					} else {
						this.toastr.error(response.Message);
					}
				},

				error: (err) => {
					console.log('API ERROR', err);
					this.toastr.error('Failed to fetch companies');
				}
			}));
	}


	onDateChange(): void {
		this.page = 1;
		this.getAllCompany();
	}

	clearDateFilter(): void {
		this.selectedDate = '';
		this.page = 1;
		this.getAllCompany();
	}

	onIndustryChange(): void {
		this.page = 1;
		this.getAllCompany();
	}

	resetFilters(): void {
		this.searchTerm = '';
		this.selectedDate = '';
		this.selectedIndustry = '';
		this.page = 1;
		this.getAllCompany();
	}


	get pendingCount(): number {
		return this.companiesData.filter(company => company.status === 'pending').length;
	}

	get needClarificationCount(): number {
		return this.companiesData.filter(company => company.status === 'need_clarification').length;
	}

	get rejectedCount(): number {
		return this.companiesData.filter(company => company.status === 'rejected').length;
	}


	closeApproveModal() {
		this.showApproveModal = false;
		this.selectedCompany = null;
	}

	confirmApproval(): void {
		this.postAcceptRejectCompanyApproval('approve');
	}


	openRejectModal(company: any) {
		this.selectedCompany = company;
		this.showRejectModal = true;
	}

	closeRejectModal() {
		this.showRejectModal = false;
		this.selectedCompany = null;
		this.rejectionReason = '';
	}


	confirmRejection() {
		if (!this.rejectionReason?.trim() || this.rejectionReason.trim().length < 5) {
			this.toastr.error('Please enter a rejection reason (minimum 5 characters)');
			return;
		}

		this.postAcceptRejectCompanyApproval('reject');
	}


	openClarificationModal(company: any) {
		this.selectedCompany = company;
		this.showClarificationModal = true;
	}


	closeClarificationModal() {
		this.showClarificationModal = false;
		this.selectedCompany = null;
		this.clarificationReason = '';
	}


	confirmClarification() {
		if (!this.clarificationReason?.trim() || this.clarificationReason.trim().length < 5) {
			this.toastr.error('Please enter clarification details (minimum 5 characters)');
			return;
		}

		this.postAcceptRejectCompanyApproval('need_clarification');
	}

	openViewModal(company: GetAllCompany): void {
		this.selectedCompany = company;
		this.showDetailedView = true;
	}


	closeViewModal(): void {
		this.showDetailedView = false;
		this.selectedCompany = null;
	}


	openViewReasonModal(company: GetAllCompany): void {
		this.selectedCompany = company;
		this.showViewReasonModal = true;
	}

	closeViewReasonModal(): void {
		this.showViewReasonModal = false;
		this.selectedCompany = null;
	}

	openViewClarificationModal(company: GetAllCompany): void {
		this.selectedCompany = company;
		this.showViewClarificationModal = true;
	}

	closeViewClarificationModal(): void {
		this.showViewClarificationModal = false;
		this.selectedCompany = null;
	}


	exportToExcel(): void {

		if (!this.companiesData?.length) {
			this.toastr.warning('No data available to export');
			return;
		}

		const exportData = this.companiesData.map((company) => ({
			'Company Name': company.company_name || '-',
			'Industry': company.industry || '-',
			'Contact Person': company.poc_name || '-',
			'Contact Email': company.poc_email || '-',
			'Official Email': company.official_email || '-',
			'Mobile': company.poc_phone || '-',
			'Status': company.status || '-',
			'Submitted Date': company.submitted_at
				? new Date(company.submitted_at).toLocaleDateString()
				: '-'
		}));

		const worksheet: XLSX.WorkSheet =
			XLSX.utils.json_to_sheet(exportData);

		const workbook: XLSX.WorkBook = {
			Sheets: {
				Companies: worksheet
			},
			SheetNames: ['Companies']
		};

		const excelBuffer = XLSX.write(workbook, {
			bookType: 'xlsx',
			type: 'array'
		});

		const blob = new Blob(
			[excelBuffer],
			{
				type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
			}
		);

		saveAs(blob, `companies_${new Date().toISOString().slice(0, 10)}.xlsx`);
		this.toastr.success('Excel exported successfully');
	}


	onSearch() {
		this.searchSubject.next(this.searchTerm?.trim() || '');
	}


	onStatusChange(status: string): void {

		this.status = status;
		this.page = 1;
		this.getAllCompany();
	}


	openApproveModal(company: any) {
		this.selectedCompany = company;
		this.showApproveModal = true;
	}


	goToCompanyApprovalDetailPage(companyId: string) {
		this.router.navigate(['admin-student-platform/company-management/approval-detail-page', companyId], { queryParams: { origin: 'company-approval', page: this.page } });
	}


	postAcceptRejectCompanyApproval(action: 'approve' | 'reject' | 'block' | 'need_clarification') {

		if (!this.selectedCompany?.company_id) {
			return;
		}

		const payload: CompanyApprovalPayload = {
			action: action,
			notes:
				action === 'approve' ? 'Company approved successfully' : (action === 'need_clarification' ? 'Need Clarification' : 'Company actioned'),
			rejection_reason: action === 'reject' ? this.rejectionReason : null,
			clarification_reason: action === 'need_clarification' ? this.clarificationReason : null
		};

		this.isApprovalLoading = true;

		this.subscription.add(
			this.companyService.postAcceptRejectCompanyApproval(payload, this.selectedCompany.company_id).pipe(
				tap((response) => {
					if (response.Success) {
						this.toastr.success(response.Data.message || response.Message);
						this.getAllCompany();
						if (action === 'approve') {
							this.closeApproveModal();
						} else if (action === 'reject') {
							this.closeRejectModal();
						} else if (action === 'need_clarification') {
							this.closeClarificationModal();
						}
					}
				})
			).subscribe({
				error: (error) => {
					this.toastr.error(error?.error?.Message || 'Something went wrong');
				},
				complete: () => {
					this.isApprovalLoading = false;
				}
			}));
	}

	sortBy(field: string): void {

		if (this.sortField === field) {
			this.sortDirection =
				this.sortDirection === 'asc' ? 'desc' : 'asc';
		}
		else {
			this.sortField = field;
			this.sortDirection = 'asc';
		}
		this.companiesData.sort((a: any, b: any) => {

			let valueA = a[field];
			let valueB = b[field];

			if (!valueA) valueA = '';
			if (!valueB) valueB = '';

			if (field === 'submitted_at') {
				valueA = new Date(valueA).getTime();
				valueB = new Date(valueB).getTime();
			}

			if (typeof valueA === 'string') {
				valueA = valueA.toLowerCase();
				valueB = valueB.toLowerCase();
			}

			if (valueA > valueB) {
				return this.sortDirection === 'asc' ? 1 : -1;
			}

			if (valueA < valueB) {
				return this.sortDirection === 'asc' ? -1 : 1;
			}

			return 0;
		});
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
		this.searchSubscription.unsubscribe();
	}
}